import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'urql';
import StickyActions from '@/components/StickyActions.jsx';
import { Field } from '@/components/Field.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import { CREAR_CUENTA, ACTUALIZAR_CUENTA } from './gql.js';

const tipoOptions = [
  { value: 'A', label: 'Activo' },
  { value: 'P', label: 'Pasivo' },
  { value: 'PN', label: 'Patrimonio Neto' },
  { value: 'I', label: 'Ingresos' },
  { value: 'G', label: 'Gastos' },
];

const monedaOptions = [
  { value: 'ARS', label: 'ARS' },
  { value: 'USD', label: 'USD' },
  { value: 'AMBAS', label: 'Ambas' },
];

export default function CuentaForm({ cuenta, cuentas, onSuccess, onCancel }) {
  const isEdit = Boolean(cuenta?.id);
  const toast = useToast();
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    tipo: 'A',
    padreId: '',
    imputable: false,
    moneda: 'ARS',
    activo: true,
  });
  const [errors, setErrors] = useState({});
  const [, createCuenta] = useMutation(CREAR_CUENTA);
  const [, updateCuenta] = useMutation(ACTUALIZAR_CUENTA);

  useEffect(() => {
    if (cuenta) {
      setForm({
        codigo: cuenta.codigo ?? '',
        nombre: cuenta.nombre ?? '',
        tipo: cuenta.tipo ?? 'A',
        padreId: cuenta.padreId ?? '',
        imputable: Boolean(cuenta.imputable),
        moneda: cuenta.moneda ?? 'ARS',
        activo: cuenta.activo !== false,
      });
    } else {
      setForm({
        codigo: '',
        nombre: '',
        tipo: 'A',
        padreId: '',
        imputable: false,
        moneda: 'ARS',
        activo: true,
      });
    }
  }, [cuenta]);

  const parentOptions = useMemo(() => {
    if (!Array.isArray(cuentas)) {
      return [];
    }
    return cuentas
      .filter((item) => !cuenta || item.id !== cuenta.id)
      .map((item) => ({
        value: item.id,
        label: `${item.codigo} · ${item.nombre}`,
      }));
  }, [cuenta, cuentas]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const currentErrors = {};
    if (!form.codigo.trim()) {
      currentErrors.codigo = 'El código es obligatorio';
    }
    if (!form.nombre.trim()) {
      currentErrors.nombre = 'El nombre es obligatorio';
    }
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }
    const payload = {
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      tipo: form.tipo,
      padreId: form.padreId || null,
      imputable: Boolean(form.imputable),
      moneda: form.moneda,
      activo: Boolean(form.activo),
    };
    try {
      const result = isEdit
        ? await updateCuenta({ id: cuenta.id, input: payload })
        : await createCuenta({ input: payload });
      if (result.error) {
        throw result.error;
      }
      toast.success(isEdit ? 'Cuenta actualizada' : 'Cuenta creada');
      onSuccess?.(result.data?.crearCuenta ?? result.data?.actualizarCuenta);
    } catch (error) {
      toast.error(error.message ?? 'Ocurrió un error al guardar la cuenta');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.9)] p-6 shadow-[0_20px_40px_-24px_rgba(var(--shadow),0.35)]">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Código" hint="Máximo 20 caracteres">
              <div className="space-y-1">
                <input
                  value={form.codigo}
                  onChange={(event) => handleChange('codigo', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.8)] px-3 py-2 text-[rgb(var(--fg))] shadow-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  maxLength={20}
                />
                {errors.codigo ? (
                  <p className="text-xs text-red-500">{errors.codigo}</p>
                ) : null}
              </div>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Nombre">
              <div className="space-y-1">
                <input
                  value={form.nombre}
                  onChange={(event) => handleChange('nombre', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.8)] px-3 py-2 text-[rgb(var(--fg))] shadow-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  maxLength={120}
                />
                {errors.nombre ? (
                  <p className="text-xs text-red-500">{errors.nombre}</p>
                ) : null}
              </div>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Tipo">
              <select
                value={form.tipo}
                onChange={(event) => handleChange('tipo', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.8)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {tipoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Moneda">
              <select
                value={form.moneda}
                onChange={(event) => handleChange('moneda', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.8)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {monedaOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Cuenta Padre">
              <select
                value={form.padreId}
                onChange={(event) => handleChange('padreId', event.target.value)}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.8)] px-3 py-2 text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Sin padre</option>
                {parentOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Configuración">
              <div className="flex items-center gap-6 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)] px-4 py-3">
                <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                  <input
                    type="checkbox"
                    checked={form.imputable}
                    onChange={(event) => handleChange('imputable', event.target.checked)}
                    className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                  />
                  Imputable
                </label>
                <label className="flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                  <input
                    type="checkbox"
                    checked={form.activo}
                    onChange={(event) => handleChange('activo', event.target.checked)}
                    className="h-4 w-4 rounded border-[rgb(var(--border))] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                  />
                  Activa
                </label>
              </div>
            </Field>
          </div>
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
          >
            {isEdit ? 'Actualizar cuenta' : 'Crear cuenta'}
          </button>
        }
      />
    </form>
  );
}
